import React from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { loadCss } from "helpers/function";
import { verifyPermission } from "helpers/permission";
import { ERROR_401 } from "config/errorPage";
import LoadingTable from "views/components/LoadingTable";
import InfirmationTable from "views/components/InfirmationTable";
import HeaderTablePage from "views/components/HeaderTablePage";
import { useProcessingCircuitData } from "hooks/configs/processing-circuit/useProcessingCircuitData";
import { usePagination } from "hooks/configs/processing-circuit/usePagination";
import { useSearch } from "hooks/configs/processing-circuit/useSearch";
import SearchInput from "components/configs/processing-circuit/SearchInput";
import InstitutionSelector from "components/configs/processing-circuit/InstitutionSelector";
import ProcessingCircuitTable from "components/configs/processing-circuit/ProcessingCircuitTable";
import ActionButtons from "components/configs/processing-circuit/ActionButtons";
import DataTableInfo from "components/configs/processing-circuit/DataTableInfo";
import Pagination from "views/components/Pagination";

loadCss("/assets/plugins/custom/datatables/datatables.bundle.css");

const ConfigProcessingCircuit = (props) => {
  const { t, ready } = useTranslation();

  React.useEffect(() => {
    document.title =
      "Satis client - " + (ready ? t("Paramètre entités de traitement") : "");
  }, [ready, t]);

  const hasPermission = React.useMemo(() => {
    return (
      verifyPermission(
        props.userPermissions,
        "update-processing-circuit-my-institution"
      ) ||
      verifyPermission(
        props.userPermissions,
        "update-processing-circuit-any-institution"
      ) ||
      verifyPermission(
        props.userPermissions,
        "update-processing-circuit-without-institution"
      )
    );
  }, [props.userPermissions]);

  React.useEffect(() => {
    if (!hasPermission) {
      window.location.href = ERROR_401;
    }
  }, [hasPermission]);

  const {
    loading,
    claimObject,
    units,
    data,
    institutionData,
    institution,
    error,
    startRequest,
    claimAutoTransferEnabled,
    onChangeProcessing,
    onSubmit,
    onChangeClaimAutoTransfer,
    onChangeInstitution,
  } = useProcessingCircuitData(props.plan, props.userPermissions);

  const { searchElement } = useSearch(claimObject);

  const {
    showList,
    numberPerPage,
    activeNumberPage,
    numberPage,
    pages,
    onChangeNumberPerPage,
    onClickPage,
    onClickNextPage,
    onClickPreviousPage,
    searchWithPagination,
  } = usePagination(claimObject, searchElement);

  const handleSearch = React.useCallback(
    (searchValue) => {
      searchWithPagination(searchValue);
    },
    [searchWithPagination]
  );

  if (!ready || !hasPermission) return null;

  return (
    <div
      className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader kt-grid__item" id="kt_subheader">
        <div className="kt-container kt-container--fluid">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">{t("Paramètres")}</h3>
            <span className="kt-subheader__separator kt-hidden" />
            <div className="kt-subheader__breadcrumbs">
              <div className="kt-subheader__breadcrumbs">
                <a href="#icone" className="kt-subheader__breadcrumbs-home">
                  <i className="flaticon2-shelter" />
                </a>
                <span className="kt-subheader__breadcrumbs-separator" />
                <a
                  href="#button"
                  onClick={(e) => e.preventDefault()}
                  className="kt-subheader__breadcrumbs-link"
                >
                  {t("Configuration entités de traitement")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
        <InfirmationTable
          information={t(
            "Paramètre de configuration des entités de traitement"
          )}
        />

        <div className="kt-portlet">
          <HeaderTablePage
            addPermission=""
            title={t("Entités de traitement")}
          />

          {loading ? (
            <LoadingTable />
          ) : (
            <div className="kt-portlet__body">
              <div
                id="kt_table_1_wrapper"
                className="dataTables_wrapper dt-bootstrap4"
              >
                <InstitutionSelector
                  userPermissions={props.userPermissions}
                  institutionData={institutionData}
                  institution={institution}
                  onChangeInstitution={onChangeInstitution}
                  error={error}
                  t={t}
                />
                <br />
                <div className="row">
                  <div className="col-sm-6 text-left order-2 order-md-1">
                    <SearchInput onSearch={handleSearch} t={t} />
                  </div>
                  <div className="d-flex justify-content-md-end col-md-6 mb-4 order-1 order-md-2">
                    <span className="kt-switch order-2 order-md-1">
                      <label>
                        <input
                          id="inactivity_control"
                          type="checkbox"
                          checked={claimAutoTransferEnabled}
                          onChange={(e) => onChangeClaimAutoTransfer(e)}
                        />
                        <span />
                      </label>
                    </span>
                    <label className="ml-md-4 mr-4 mr-md-0 col-form-label order-1 order-md-2">
                      {t("Activer l'auto-affectation des réclamations")}{" "}
                      {/* <InputRequire /> */}
                    </label>
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-12">
                    <br />
                    <ProcessingCircuitTable
                      claimObject={claimObject}
                      showList={showList}
                      data={data}
                      units={units}
                      onChangeProcessing={onChangeProcessing}
                      t={t}
                    />

                    <ActionButtons
                      startRequest={startRequest}
                      onSubmit={onSubmit}
                      t={t}
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-12 col-md-5">
                    <DataTableInfo
                      numberPerPage={numberPerPage}
                      totalItems={claimObject.length}
                      shownItems={showList?.length}
                      t={t}
                    />
                  </div>
                  {showList.length > 0 && (
                    <div className="col-sm-12 col-md-7 dataTables_pager">
                      <Pagination
                        numberPerPage={numberPerPage}
                        onChangeNumberPerPage={onChangeNumberPerPage}
                        activeNumberPage={activeNumberPage}
                        onClickPreviousPage={onClickPreviousPage}
                        pages={pages}
                        onClickPage={onClickPage}
                        numberPage={numberPage}
                        onClickNextPage={onClickNextPage}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userPermissions: state.user.user.permissions,
  plan: state.plan.plan,
});

export default connect(mapStateToProps)(ConfigProcessingCircuit);
